import React, { useEffect, useState, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import EditScheduleModal from '../../components/Schedule/Modals/EditSchedule';
import {
    Button,
    EMPTY_IMAGE,
    LoadingSpinner,
    SelectList,
    RowContainer,
    Back,
    Typography,
    ColContainer,
} from '../../components/common';
import { SkeletonTable } from '../../components/common/Skeletons';
import toast from '../../components/common/toast';
import { UserContext } from '../../contexts/user';
import { GetRoutingProfiles } from '../../services/Api/routingProfiles/useApiRoutingProfiles';
import {
    GetScheduleItineraryByScheduleId,
    PostScheduleItineraryChanges,
} from '../../services/Api/scheduleItineries/useApiScheduleItineries';
import {
    DeleteSchedule,
    GetScheduleById,
    UpdateSchedule,
} from '../../services/Api/schedules/useApiSchedules';
import { dayMapper, getTimeFromMins } from '../../services/Helpers/dateHelper';
import IconComponent from '../../components/Icon';

export default function Schedule() {
    const { id: scheduleId } = useParams();
    const { hasRolePermission } = useContext(UserContext);
    const [schedule, setSchedule] = useState(null);
    const [itinerary, setItinerary] = useState([]);
    const [showEditScheduleForm, setShowEditScheduleForm] = useState(false);
    const [routingProfiles, setRoutingProfiles] = useState([]);
    const [isLoading, setIsLoading] = useState({ schedule: true, itinerary: true });
    const router = useHistory();

    const canEdit = hasRolePermission('se');
    const canDelete = hasRolePermission('sd');

    const updateScheduleInfo = (info) => {
        setSchedule({
            ...schedule,
            name: info.name,
            description: info.description,
        });
    };

    //Get Schedule Name and Description
    useEffect(() => {
        GetScheduleById({ scheduleId: scheduleId })
            .then((response) => {
                if (response.data) {
                    setSchedule(response.data);
                    setIsLoading((l) => ({ ...l, schedule: false }));
                }

                GetRoutingProfiles({ instanceId: response.data.instanceid })
                    .then((response) => {
                        if (response.data) {
                            setRoutingProfiles(
                                response.data.RoutingProfileSummaryList.map((item) => ({
                                    label: item.Name,
                                    value: item.Id,
                                }))
                            );

                            GetScheduleItineraryByScheduleId({ scheduleId: scheduleId })
                                .then((response) => {
                                    if (response.data) {
                                        setItinerary(response.data);
                                        setIsLoading((l) => ({ ...l, itinerary: false }));
                                    }
                                })
                                .catch((error) => {
                                    setIsLoading((l) => ({ ...l, itinerary: false }));
                                });
                        }
                    })
                    .catch((error) => {
                        setIsLoading((l) => ({ ...l, itinerary: false }));
                    });
            })
            .catch((error) => {
                setIsLoading({ schedule: false, itinerary: false });
            });
    }, [scheduleId]);

    const getRoutingProfileValueAtDayTime = (day, time) => {
        const item = itinerary.find((i) => i.day === day && i.time === time);
        if (item) {
            const returnItem = {
                routingProfile: {
                    value: item.routingprofileid,
                    label: item?.routingprofileid
                        ? routingProfiles.find((i) => i?.value === item?.routingprofileid)?.label
                        : '',
                },
                acw: item.acw,
                autoAccept: item.autoaccept,
            };
            return returnItem;
        }
        return null;
    };

    const startTime = 8 * 60;
    const endTime = 20 * 60;

    const [apiTable, setApiTable] = useState([]);

    useEffect(() => {
        const apiTableTemplate = [];

        for (let t = startTime; t <= endTime; t += 15) {
            const displayTime = getTimeFromMins(t);
            for (let d = 0; d <= 6; d++) {
                const current = getRoutingProfileValueAtDayTime(d, t);
                const row = {
                    time: t,
                    displayTime,
                    day: d,
                    originalRoutingProfileId: current?.routingProfile.value,
                    routingProfileId: current?.routingProfile.value,
                    originalAcw: current?.acw,
                    acw: current?.acw,
                    originalAutoAccept: current?.autoAccept,
                    autoAccept: current?.autoAccept,
                    change: '',
                };
                apiTableTemplate.push(row);
            }
        }

        setApiTable(apiTableTemplate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itinerary]);

    const updateSetting = (day, time, { property = '', newValue = null }) => {
        const apiTableCopy = [...apiTable];
        const itemCopyIndex = apiTable.findIndex((i) => i.day === day && i.time === time);

        apiTableCopy[itemCopyIndex].scheduleId = schedule.id;

        apiTableCopy[itemCopyIndex][property] = newValue;

        if (property === 'change') {
            setApiTable(apiTableCopy);
            return;
        }

        if (
            apiTableCopy[itemCopyIndex].originalRoutingProfileId ===
                apiTableCopy[itemCopyIndex].routingProfileId &&
            apiTableCopy[itemCopyIndex].originalAcw === apiTableCopy[itemCopyIndex].acw &&
            apiTableCopy[itemCopyIndex].originalAutoAccept ===
                apiTableCopy[itemCopyIndex].autoAccept
        ) {
            apiTableCopy[itemCopyIndex].change = '';
        } else if (
            !apiTableCopy[itemCopyIndex].originalRoutingProfileId &&
            !apiTableCopy[itemCopyIndex].originalAcw &&
            !apiTableCopy[itemCopyIndex].originalAutoAccept &&
            (apiTableCopy[itemCopyIndex].routingProfileId ||
                apiTableCopy[itemCopyIndex].acw ||
                apiTableCopy[itemCopyIndex].autoAccept)
        ) {
            apiTableCopy[itemCopyIndex].change = 'created';
            if (!apiTableCopy[itemCopyIndex].acw) apiTableCopy[itemCopyIndex].acw = 0;
            if (!apiTableCopy[itemCopyIndex].autoAccept)
                apiTableCopy[itemCopyIndex].autoAccept = false;
        } else if (
            !apiTableCopy[itemCopyIndex].routingProfileId &&
            !apiTableCopy[itemCopyIndex].acw &&
            !apiTableCopy[itemCopyIndex].autoAccept &&
            (apiTableCopy[itemCopyIndex].originalRoutingProfileId ||
                apiTableCopy[itemCopyIndex].originalAcw ||
                apiTableCopy[itemCopyIndex].originalAutoAccept)
        ) {
            apiTableCopy[itemCopyIndex].change = 'removed';
        } else {
            apiTableCopy[itemCopyIndex].change = 'changed';
            if (!apiTableCopy[itemCopyIndex].acw) apiTableCopy[itemCopyIndex].acw = 0;
            if (!apiTableCopy[itemCopyIndex].autoAccept)
                apiTableCopy[itemCopyIndex].autoAccept = false;
        }

        setApiTable(apiTableCopy);
    };

    const handleDelete = () => {
        DeleteSchedule(scheduleId)
            .then((response) => {
                if (response && response.status === 200) {
                    router.goBack();
                    toast.success('Schedule Deleted');
                }
            })
            .catch((err) => {
                toast.error();
                console.error(err);
            });
    };

    const handleSubmit = () => {
        var postChanges = apiTable
            .filter((row) => row.change !== '')
            .map(({ scheduleId, day, time, routingProfileId, acw, autoAccept, change }) => ({
                scheduleId,
                day,
                time,
                routingProfileId,
                acw,
                autoAccept,
                change,
            }));

        UpdateSchedule({ scheduleId }, { name: schedule.name, description: schedule.description })
            .then((response) => {
                if (response && response.status === 200) {
                    if (postChanges && postChanges.length) {
                        PostScheduleItineraryChanges(postChanges).then((response) => {
                            if (response && response?.status === 200) {
                                toast.success('Schedule Saved');
                            } else {
                                toast.error();
                            }
                        });
                    } else {
                        toast.success('Schedule Saved');
                    }
                }
            })
            .catch((err) => {
                console.error(err);
                toast.error();
            });
    };

    const DayTimeCell = ({ day, time }) => {
        const data = apiTable.find((i) => i.day === day && i.time === time);
        const routingProfile = routingProfiles.find((rp) => rp.value === data.routingProfileId);

        const handleUpdate = (property, newValue) => {
            updateSetting(day, time, { property, newValue });
        };

        if (
            (typeof data.acw === 'undefined' &&
                typeof data.autoAccept === 'undefined' &&
                !data.routingProfileId) ||
            data.change === 'removed'
        ) {
            return (
                <td
                    colSpan={3}
                    className={`group h-full transition-all ${
                        canEdit && 'cursor-pointer'
                    } border-r border-grey-light`}
                    onClick={() => canEdit && handleUpdate('autoAccept', false)}
                >
                    {canEdit ? (
                        <div className="hidden justify-center group-hover:flex">
                            <Button type="icon">
                                <IconComponent icon="add" height={14} width={14} />
                            </Button>
                        </div>
                    ) : null}
                </td>
            );
        }

        return (
            <td colSpan={3} className="border-r border-grey-light">
                <RowContainer noFlex className="bg-white" yPadding="small" xPadding="x-small">
                    <SelectList
                        alternateStyle={2}
                        defaultValue={routingProfile}
                        onChange={(e) =>
                            updateSetting(day, time, {
                                property: 'routingProfileId',
                                newValue: e?.value,
                            })
                        }
                        placeholder={'Select a routing profile'}
                        // isClearable={true}
                        options={routingProfiles}
                    />
                    <RowContainer className="flex-col content-center justify-between gap-4 p-0 2xl:flex-row 2xl:gap-0.5">
                        <Typography className="mt-1" variant="body3">
                            <span className="text-grey-dark">ACW:</span>{' '}
                            <input
                                className="outline-none pl-2"
                                type="number"
                                min={0}
                                max={99}
                                step={1}
                                defaultValue={data?.acw}
                                onBlur={(e) =>
                                    updateSetting(day, time, {
                                        property: 'acw',
                                        newValue: e?.target?.value
                                            ? parseInt(e.target.value)
                                            : null,
                                    })
                                }
                            />
                        </Typography>
                        <Button
                            onClick={() => handleUpdate('autoAccept', !data?.autoAccept)}
                            type="toggle"
                            className={`w-max py-0.5 px-3 text-12/18 ${
                                data?.autoAccept ? 'bg-brand-main' : 'bg-grey-normal'
                            }`}
                        >
                            {data?.autoAccept ? 'auto' : 'manual'}
                        </Button>
                    </RowContainer>
                    <RowContainer className="pt-4">
                        <IconComponent
                            icon="bin"
                            height={14}
                            width={14}
                            className="cursor-pointer px-1 text-grey-dark hover:text-error"
                            onClick={() =>
                                updateSetting(day, time, {
                                    property: 'change',
                                    newValue: 'removed',
                                })
                            }
                        />
                    </RowContainer>
                </RowContainer>
            </td>
        );
    };

    return (
        <div className="w-full grid-cols-1 pb-24">
            {canEdit && showEditScheduleForm ? (
                <div className="flex w-full justify-center">
                    <EditScheduleModal
                        defaultValues={schedule}
                        onSave={(values) => {
                            updateScheduleInfo(values);
                            setShowEditScheduleForm(false);
                        }}
                        closeForm={() => setShowEditScheduleForm(false)}
                    />
                </div>
            ) : (
                <></>
            )}

            {isLoading.schedule ? (
                <LoadingSpinner />
            ) : (
                <>
                    <Back>Back to List of Schedules</Back>
                    <RowContainer className="mb-6 items-center justify-between">
                        <RowContainer spacing="medium" className="items-center">
                            <img
                                style={{ maxWidth: '64px', height: 'auto' }}
                                src={`data:image/png;base64, ${
                                    schedule.logo || EMPTY_IMAGE.base64
                                }`}
                                alt={schedule.company}
                            />
                            <ColContainer spacing="small">
                                <Typography variant="heading1.1">{schedule.name}</Typography>
                                <Typography variant="heading2.3">
                                    {schedule.company} ({schedule.project})
                                </Typography>
                            </ColContainer>
                        </RowContainer>
                        <ColContainer spacing="small">
                            <Typography variant="body2" color="grey-dark">
                                Description
                            </Typography>
                            <Typography variant="body1.2">{schedule.description}</Typography>
                        </ColContainer>
                        <RowContainer spacing="small">
                            <Button
                                type="delete"
                                disabled={!canDelete || isLoading.schedule || isLoading.itinerary}
                                onClick={() => handleDelete()}
                            >
                                Delete
                            </Button>
                            <Button
                                type="secondary"
                                disabled={!canEdit || isLoading.schedule}
                                onClick={() => setShowEditScheduleForm(true)}
                            >
                                Edit Name/Description
                            </Button>
                            <Button
                                disabled={!canEdit || isLoading.schedule || isLoading.itinerary}
                                type="primary"
                                onClick={() => handleSubmit()}
                            >
                                Save
                            </Button>
                        </RowContainer>
                    </RowContainer>
                </>
            )}

            {isLoading.itinerary ? (
                <SkeletonTable columns={7} rows={35} />
            ) : (
                <table className="w-full table-fixed">
                    <thead>
                        <tr key="table-header" className="border-b border-grey-light">
                            <th rowSpan={2} className="w-32 border-r border-grey-light"></th>
                            <HeaderCell>Monday</HeaderCell>
                            <HeaderCell>Tuesday</HeaderCell>
                            <HeaderCell>Wednesday</HeaderCell>
                            <HeaderCell>Thursday</HeaderCell>
                            <HeaderCell>Friday</HeaderCell>
                            <HeaderCell>Saturday</HeaderCell>
                            <HeaderCell>Sunday</HeaderCell>
                        </tr>
                    </thead>
                    <tbody>
                        {apiTable
                            .filter((i) => i.day === 0) // filter to only iterate over one days worth of times!
                            .map((row) => {
                                return (
                                    <tr key={row.time} className="h-32 border-b border-grey-light">
                                        <th className="border-r border-grey-light p-6">
                                            <Typography variant="heading2.1">
                                                {row.displayTime}
                                            </Typography>
                                        </th>
                                        <DayTimeCell
                                            key={dayMapper.monday.number}
                                            day={dayMapper.monday.number}
                                            time={row.time}
                                        />
                                        <DayTimeCell
                                            key={dayMapper.tuesday.number}
                                            day={dayMapper.tuesday.number}
                                            time={row.time}
                                        />
                                        <DayTimeCell
                                            key={dayMapper.wednesday.number}
                                            day={dayMapper.wednesday.number}
                                            time={row.time}
                                        />
                                        <DayTimeCell
                                            key={dayMapper.thursday.number}
                                            day={dayMapper.thursday.number}
                                            time={row.time}
                                        />
                                        <DayTimeCell
                                            key={dayMapper.friday.number}
                                            day={dayMapper.friday.number}
                                            time={row.time}
                                        />
                                        <DayTimeCell
                                            key={dayMapper.saturday.number}
                                            day={dayMapper.saturday.number}
                                            time={row.time}
                                        />
                                        <DayTimeCell
                                            key={dayMapper.sunday.number}
                                            day={dayMapper.sunday.number}
                                            time={row.time}
                                        />
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
            )}
        </div>
    );
}

const HeaderCell = ({ children }) => {
    return (
        <th colSpan={3} className="w-1/5 border-r border-grey-light p-6">
            <RowContainer
                xPadding="large"
                yPadding="medium"
                cornerRadius="large"
                className="justify-center bg-grey-light"
            >
                <Typography variant="heading2.2" color="grey-dark">
                    {children}
                </Typography>
            </RowContainer>
        </th>
    );
};
